import { Controller } from '@hotwired/stimulus';
import Quill from 'quill';

const toolbarOptions = [
  [{ 'size': [] }],
  ['bold', 'italic', 'underline', 'link', 'video', 'strike'],
  [
    { 'color': ['#000000', '#FFFFFF', '#E60000', '#FF9900', '#FFFF00', '#008A00', '#0066CC', '#9933FF'] },
    { 'background': ['#FFFFFF', '#FACCCC', '#FFEBCC', '#FFFFCC', '#CCE8CC', '#CCE0F5', '#EBD6FF', '#BBBBBB'] }
  ],
  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
  [{ 'align': [] }],
  ['divider', 'clean']
];

const emailNoticeTollbarOptions = [
  [{ 'size': [] }],
  ['bold', 'italic', 'underline', 'link', 'strike'],
  [
    { 'color': ['#000000', '#FFFFFF', '#E60000', '#FF9900', '#FFFF00', '#008A00', '#0066CC', '#9933FF'] },
    { 'background': ['#FFFFFF', '#FACCCC', '#FFEBCC', '#FFFFCC', '#CCE8CC', '#CCE0F5', '#EBD6FF', '#BBBBBB'] }
  ],
  [{ 'list': 'bullet' }], [{ 'align': [] }], ['divider', 'clean']
];

const minimalToolbarOptions = [];

const TOOLBAR_STYLES = {
  default: toolbarOptions,
  minimal: minimalToolbarOptions,
  email: emailNoticeTollbarOptions
};

export default class extends Controller {
  static targets = ['input', 'container', 'textContainer'];

  declare inputTarget: HTMLInputElement;
  declare containerTarget: HTMLElement;
  declare textContainerTarget: HTMLElement;

  private saved = true;
  private quill: Quill;

  public initialize(): void {
    this.initializeQuill();

    // Remove trailing empty BreakBlot paragraph Quill adds on render, then sync hidden input.
    setTimeout(() => {
      this.removeTrailingBreaks();
      this.inputTarget.value = this.getEditorContent();
    }, 0);

    setInterval(this.updateInputValueWithQuillContent.bind(this), 2000);

    window.onbeforeunload = () => {
      if (!this.saved) {
        return 'There are unsaved changes. Are you sure you want to leave?';
      }
    };

    const parentComponent = window.EB.RichTextFields && window.EB.RichTextFields[this.containerTarget.dataset.id];
    parentComponent?.childrenInitCallback?.(this);
  }

  private initializeQuill(): void {
    this.quill = new Quill(this.textContainerTarget, this.getOptions());

    // Intercept Shift+Enter before Quill handles it
    const editorElement = this.textContainerTarget.querySelector('.ql-editor') as HTMLElement;
    if (editorElement) {
      editorElement.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' && e.shiftKey) {
          // Prevent Quill from receiving the event and creating a new <p> paragraph
          e.stopImmediatePropagation();
          e.preventDefault();

          // Insert a native line break at the cursor position
          document.execCommand('insertLineBreak');

          // Force Quill to sync the manually modified DOM with its internal model
          this.quill.update();
        }
      }, true); // capture mode: runs before Quill's own listener
    }

    this.quill.on('text-change', () => {
      this.saved = false;
      this.emitContentChangeEvent();
    });

    if (this.containerTarget.dataset.disabled === 'true') {
      this.quill.enable(false);
    }

    this.addToolbarTooltips();
    this.registerVideoHandler();
  }

  private emitContentChangeEvent(): void {
    const isEmpty = this.isEditorEmpty();

    const event = new CustomEvent('quill:content-changed', {
      detail: {
        isEmpty: isEmpty,
        content: this.quill.getText()
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  private isEditorEmpty(): boolean {
    const text = this.quill.getText().trim();
    const hasImages = this.textContainerTarget.querySelector('.ql-editor img') !== null;
    const hasVideos = this.textContainerTarget.querySelector('.ql-editor iframe') !== null;

    return text === '' && !hasImages && !hasVideos;
  }

  private updateInputValueWithQuillContent(): void {
    if (this.saved) return;

    const content = this.getEditorContent();
    this.inputTarget.value = content;

    // TODO: using jquery here as a workaround
    // need go investigate why 'dispatchEvent(new Event('change'))' is not working for all forms
    $('.rich_text_textarea').trigger('change');
    this.saved = true;
  }

  private removeTrailingBreaks(): void {
    const length = this.quill.getLength();
    if (length <= 1) return;

    const [lastLine] = this.quill.getLine(length - 1);
    if (!lastLine) return;

    const lineStart = this.quill.getIndex(lastLine);
    const lineContentLength = (lastLine as any).length() - 1; // exclude block's \n
    if (lineContentLength <= 0) return;

    // Only remove if the paragraph has no actual text (only BreakBlots / sentinel spans)
    const lastP = (lastLine as any).domNode as HTMLElement;
    const walker = document.createTreeWalker(lastP, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode()) !== null) {
      if ((node.textContent ?? '').trim() !== '') return;
    }
    if (lastP.querySelector('img, iframe')) return;

    this.quill.deleteText(lineStart, lineContentLength, (Quill as any).sources.SILENT);
  }

  private getEditorContent(): string {
    const root = this.textContainerTarget.querySelector('.ql-editor');
    if (!root) return '';

    const html = root.innerHTML;

    // Scan backwards to find the last actual text character (not inside a tag, not whitespace).
    let lastTextPos = -1;
    let insideTag = false;
    for (let i = html.length - 1; i >= 0; i--) {
      const char = html[i];
      if (char === '>') { insideTag = true; continue; }
      if (char === '<') { insideTag = false; continue; }
      if (!insideTag && char.trim() !== '') { lastTextPos = i; break; }
    }

    if (lastTextPos === -1) return '';

    // Keep everything up to the last text character, then re-append only the
    // closing tags that immediately follow it so the HTML stays well-formed.
    const trailing = html.substring(lastTextPos + 1);
    const closingTags = trailing.match(/^(<\/[a-zA-Z][^>]*>)+/)?.[0] ?? '';
    return html.substring(0, lastTextPos + 1) + closingTags;
  }

  private getOptions(): any {
    const Delta = Quill.import('delta');
    return {
      modules: {
        toolbar: TOOLBAR_STYLES[this.containerTarget.dataset.toolbarStyle] || TOOLBAR_STYLES.default,
        clipboard: {
          matchers: [
            ['BR', (_node: Node, _delta: unknown) => new Delta().insert({ break: true })]
          ]
        }
      },
      theme: 'snow',
      placeholder: this.containerTarget.dataset.placeholder || ''
    };
  }

  private registerVideoHandler(): void {
    const toolbar = this.quill.getModule('toolbar');
    toolbar.addHandler('video', this.videoHandler.bind(this));
  }

  private videoHandler(): void {
    const range = this.quill.getSelection(true);
    const url = prompt('Enter the video URL:');

    if (url) {
      const adjustedUrl = this.adjustVideoUrl(url);
      this.quill.insertEmbed(range.index, 'video', adjustedUrl, Quill.sources.USER);
      this.saved = false;
    }
  }

  private adjustVideoUrl(url: string): string {
    // YouTube (full and minified URLs)
    const ytRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?(.*&)?v=|youtube\.com\/embed\/|youtu\.be\/)([\w\-]{11})(\?.*)?$/;
    const ytMatch = url.match(ytRegex);
    if (ytMatch && ytMatch[2]) {
      const videoId = ytMatch[2];
      const queryParams = ytMatch[3] || '';
      return `https://www.youtube.com/embed/${videoId}${queryParams}`;
    }

    // Vimeo
    const vimeoRegex = /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:.*\/)?(\d+)(?:$|\/|\?)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch && vimeoMatch[1]) {
      const videoId = vimeoMatch[1];
      return `https://player.vimeo.com/video/${videoId}`;
    }

    // Wistia
    const wistiaRegex = /^(https?:\/\/(?:[\w\.-]+\.wistia\.com))\/(medias|embed)\/([a-zA-Z0-9]+)/;
    const wistiaMatch = url.match(wistiaRegex);
    if (wistiaMatch && wistiaMatch[1] && wistiaMatch[3]) {
      const domain = wistiaMatch[1];
      const videoId = wistiaMatch[3];
      return `${domain}/embed/iframe/${videoId}`;
    }

    // Dailymotion
    const dmRegex = /^(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/video\/([a-zA-Z0-9]+)/;
    const dmMatch = url.match(dmRegex);
    if (dmMatch && dmMatch[1]) {
      const videoId = dmMatch[1];
      return `https://www.dailymotion.com/embed/video/${videoId}`;
    }

    return url;
  }

  private addToolbarTooltips(): void {
    const tooltips = [
        { selector: '.ql-picker-label', title: 'Font Size' },
        { selector: 'button.ql-bold', title: 'Bold' },
        { selector: 'button.ql-italic', title: 'Italic' },
        { selector: 'button.ql-underline', title: 'Underline' },
        { selector: 'button.ql-link', title: 'Link' },
        { selector: 'button.ql-strike', title: 'Strike' },
        { selector: '.ql-color.ql-color-picker', title: 'Color' },
        { selector: '.ql-background.ql-color-picker', title: 'Background Color' },
        { selector: 'button.ql-list', title: 'Ordered List' },
        { selector: 'button.ql-list[value="bullet"]', title: 'Bullet List' },
        { selector: '.ql-align', title: 'Align' },
        { selector: 'button.ql-video', title: 'Video' }
    ];

    tooltips.forEach(({ selector, title }) => {
        const element = this.containerTarget.querySelector(selector);
        if (element) {
            element.setAttribute('title', title);
        }
    });
  }
}
